import CancelButton from "@common/controls/Form/Buttons/Cancel";
import SubmitButton from "@common/controls/Form/Buttons/Submit";
import { FormButtons, FormField } from "@common/controls/Form/component";
import { FancyCheckbox } from "@common/controls/Inputs/Checkbox/component";
import Select from "@common/controls/Inputs/Select/component";
import TextInput from "@common/controls/Inputs/Text/component";
import {
  type FormModalControlledProps,
  type FormModalControlledRenderParams
} from "@common/controls/Modal/Modals/Form/component";
import { useI18n } from "@common/hooks/i18n";
import getOptions from "@common/lib/options";
import { validators } from "@common/lib/validate";
import classNames from "classnames";
import React from "react";
import Modal from "../Modal";

export type LinkModalFormData = {
  isNewWindow: boolean;
  isMailto: boolean;
  text: string;
  url: string;
};
type Props = Omit<FormModalControlledProps<LinkModalFormData>, "children" | "isUrl"> & {
  dropdownClassName?: string;
};
const LinkModal = ({ className, dropdownClassName, ...props }: Props) => {
  const i18n = useI18n();

  const options = React.useMemo(() => getOptions(i18n), [i18n]);

  const defaultValues = React.useMemo(
    (): Partial<LinkModalFormData> => ({
      ...props.defaultValues,
      isMailto: props.defaultValues.url && !validators.mailTo(i18n, props.defaultValues.url) ? true : false,
      url: props.defaultValues.url?.startsWith("mailto")
        ? props.defaultValues.url.replace(/^\s*mailto\:\s*/i, "")
        : props.defaultValues.url
    }),
    [props.defaultValues, i18n]
  );

  const submit = React.useCallback(
    (data: LinkModalFormData) => {
      props.submit({
        ...data,
        url: data.isMailto ? `mailto:${data.url.trim()}` : data.url.trim()
      });
    },
    [props.submit]
  );

  return (
    <Modal
      {...props}
      defaultValues={defaultValues}
      className={classNames(className, "rich_editor_modal")}
      submit={submit}
    >
      {({ values }: FormModalControlledRenderParams<LinkModalFormData>) => (
        <>
          <FormField label={i18n.text} name="text" Component={TextInput} value={values.text} />
          <FormField
            isRequired
            label={i18n.type}
            name="isMailto"
            Component={Select}
            componentProps={{
              dropdownClassName,
              options: options.yesNo(i18n.mailto, i18n.url)
            }}
            value={values.isMailto}
          />

          <FormField
            label={values.isMailto ? i18n.email : i18n.url}
            name="url"
            Component={TextInput}
            value={values.url}
            validator={values.isMailto ? validators.email : validators.url}
          />

          {!values.isMailto && (
            <FormField
              name="isNewWindow"
              Component={FancyCheckbox}
              value={values.isNewWindow}
              componentProps={{ label: i18n.openInNewTab }}
            />
          )}

          <FormButtons>
            <CancelButton />
            <SubmitButton />
          </FormButtons>
        </>
      )}
    </Modal>
  );
};
export default LinkModal;
